import * as React from 'react';
import { MachineContext } from "../fsm/useMachine"
import { SET_BORDER } from "../fsm/types"
import ImageFlowObject from "../../model/ImageFlowObject"
import PdfFlowObject from "../../model/PdfFlowObject"
import VideoFlowObject from "../../model/VideoFlowObject"
import ShapeFlowObject from "../../model/ShapeFlowObject"
import Color from '../../model/Color';
import { getShapeDefinition } from "../../model/ShapeDefinitions";

type BorderTarget = ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject;

const SWATCHES: { label: string; color: Color | null }[] = [
    { label: "None", color: null },
    { label: "Black", color: Color.black() },
    { label: "White", color: Color.white() },
    { label: "Red", color: Color.fromHex("#FF0000") },
    { label: "Blue", color: Color.fromHex("#0066FF") },
    { label: "Green", color: Color.fromHex("#00AA44") },
    { label: "Yellow", color: Color.fromHex("#FFD600") },
    { label: "Orange", color: Color.fromHex("#FF8800") },
    { label: "Purple", color: Color.fromHex("#8833CC") },
];

export const BorderPicker: React.FC<{ close: () => void }> = ({ close }) => {
    const [current, send] = React.useContext(MachineContext)
    const currentObject = current.context.currentObject! as BorderTarget
    const [width, setWidth] = React.useState(currentObject.borderWidth)
    const [selectedColor, setSelectedColor] = React.useState<Color | null>(
        currentObject.borderWidth > 0 ? currentObject.borderColor : null
    )

    const isShape = currentObject.type === "shape";

    const imgSrc = React.useMemo(() => {
        if (isShape) return undefined;
        const av = currentObject as (ImageFlowObject | VideoFlowObject | PdfFlowObject);
        if (av.croppedImageAsset?.externalURL.__tag === "string") {
            return av.croppedImageAsset!.externalURL.value
        } else if (av.originalImageAsset?.externalURL.__tag === "string") {
            return av.originalImageAsset!.externalURL.value
        }
    }, [current.context.currentObject])

    const dispatch = (color: Color | null, w: number) => {
        const borderColor = color ?? Color.black();
        const borderWidth = color === null ? 0 : w;
        send({ type: "DELEGATE", payload: { type: SET_BORDER, color: borderColor, width: borderWidth }, id: currentObject.id })
    }

    const handleSwatchClick = (swatch: { label: string; color: Color | null }) => {
        setSelectedColor(swatch.color);
        if (swatch.color === null) {
            setWidth(0);
            dispatch(null, 0);
        } else {
            const w = width > 0 ? width : 3;
            setWidth(w);
            dispatch(swatch.color, w);
        }
    }

    const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const w = parseInt(e.target.value, 10);
        setWidth(w);
        if (w === 0) {
            setSelectedColor(null);
            dispatch(null, 0);
        } else {
            const c = selectedColor ?? Color.black();
            setSelectedColor(c);
            dispatch(c, w);
        }
    }

    const preview = (borderStyle: string) => {
        if (isShape) {
            const shape = currentObject as ShapeFlowObject;
            const sz = 48;
            const bw = selectedColor && width > 0 ? width : 0;
            const pad = bw + 2;
            const inner = sz - pad * 2;
            const fillColor = shape.color.asRgba();
            const strokeColor = selectedColor ? selectedColor.asRgba() : "none";
            const def = getShapeDefinition(shape.shapeType);

            let svgChild: React.ReactNode;
            if (shape.shapeType === "ellipse") {
                svgChild = <ellipse cx={sz / 2} cy={sz / 2} rx={inner / 2} ry={inner / 2} />;
            } else if (shape.shapeType === "rectangle") {
                const cr = shape.cornerRadius > 0 ? Math.min(shape.cornerRadius, inner / 2) : 0;
                svgChild = <rect x={pad} y={pad} width={inner} height={inner} rx={cr} ry={cr} />;
            } else if (shape.shapeType === "roundedRectangle") {
                const cr = shape.cornerRadius > 0 ? Math.min(shape.cornerRadius, inner / 2) : inner * 5 / 28;
                svgChild = <rect x={pad} y={pad} width={inner} height={inner} rx={cr} ry={cr} />;
            } else if (def?.renderType === "path" && def.pathString) {
                const scaled = def.pathString.replace(
                    /(\d+\.?\d*)\s+(\d+\.?\d*)/g,
                    (_m, x, y) => `${parseFloat(x) * inner + pad} ${parseFloat(y) * inner + pad}`
                );
                svgChild = <path d={scaled} fillRule="evenodd" />;
            } else if (shape.pointsArrayString) {
                const points = shape.pointsArrayString
                    .split(" ")
                    .map(pair => {
                        const [x, y] = pair.split(",");
                        return `${parseFloat(x) * inner + pad},${parseFloat(y) * inner + pad}`;
                    })
                    .join(" ");
                svgChild = <polygon points={points} />;
            } else {
                svgChild = <rect x={pad} y={pad} width={inner} height={inner} />;
            }

            return (
                <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`}>
                    <g
                        style={{
                            fill: fillColor,
                            stroke: strokeColor,
                            strokeWidth: bw > 0 ? bw : 0,
                        }}
                    >
                        {svgChild}
                    </g>
                </svg>
            );
        }
        return <img src={imgSrc} style={{ width: 48, height: 48, objectFit: "cover", border: borderStyle, boxSizing: "border-box" }} />;
    }

    return (
        <div id="borderPicker" style={{ padding: 8, minWidth: 200 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {SWATCHES.map((swatch) => {
                    const isActive = swatch.color === null
                        ? selectedColor === null
                        : selectedColor !== null && swatch.color.equals(selectedColor);
                    return (
                        <div
                            key={swatch.label}
                            title={swatch.label}
                            onClick={() => handleSwatchClick(swatch)}
                            style={{
                                width: 28, height: 28,
                                borderRadius: 4,
                                cursor: "pointer",
                                backgroundColor: swatch.color ? swatch.color.asRgba() : "transparent",
                                border: isActive ? "2px solid #007AFF" : "1px solid #ccc",
                                position: "relative",
                            }}
                        >
                            {swatch.color === null && (
                                <svg width="28" height="28" viewBox="0 0 28 28" style={{ position: "absolute", top: -1, left: -1 }}>
                                    <line x1="0" y1="28" x2="28" y2="0" stroke="#FF3B30" strokeWidth="2" />
                                </svg>
                            )}
                        </div>
                    );
                })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <label style={{ fontSize: 12, minWidth: 40 }}>Width</label>
                <input
                    type="range"
                    min={0} max={5} step={1}
                    value={width}
                    onChange={handleWidthChange}
                    style={{ flex: 1 }}
                />
                <span style={{ fontSize: 12, minWidth: 16, textAlign: "right" }}>{width}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
                {preview(selectedColor && width > 0 ? `${width}px solid ${selectedColor.asRgba()}` : "none")}
            </div>
        </div>
    )
}

export function borderToIcon(val: { color: Color, width: number }) {
    if (val.width === 0) {
        return "borderColor-none.png"
    }
    if (val.color.equals(Color.black())) {
        return "borderColor-black.png"
    }
    return "borderColor-white.png"
}
